import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaUser, FaCalendarAlt, FaDownload, FaList } from "react-icons/fa";
import * as XLSX from 'xlsx';
import '../styles/EmployeeAttendanceCalendar.css';
import AttendanceStrategyFactory from '../services/attendance/AttendanceStrategyFactory';

function EmployeeAttendanceCalendar() {
     const location = useLocation();
     const navigate = useNavigate();
     const token = sessionStorage.getItem("token") || localStorage.getItem("token");
     const BASE_URL = process.env.REACT_APP_BASE_URL;

     // Get passed state from List View
     const passedState = location.state || {};
     const [employees, setEmployees] = useState([]);
     const [selectedEmployee, setSelectedEmployee] = useState(passedState.selectedEmployee || "");
     const [attendance, setAttendance] = useState({ days: [] });
     const [selectedMonth, setSelectedMonth] = useState(passedState.selectedMonth || "");
     const [employeeInputValue, setEmployeeInputValue] = useState(
          passedState.selectedEmployeeName
               ? `${passedState.selectedEmployee} - ${passedState.selectedEmployeeName}`
               : ""
     );
     const [popupDetails, setPopupDetails] = useState({ show: false, punchIn: '', punchOut: '', day: null, position: {}, workHours: '' });
     const [employeeType, setEmployeeType] = useState(passedState.employeeType || "");
     const [attendanceStrategy, setAttendanceStrategy] = useState(null);
     const [isLoading, setIsLoading] = useState(false);
     const selectedEmployeeObj = employees.find(emp => emp.employee_code === selectedEmployee);
     const selectedEmployeeName = selectedEmployeeObj ? selectedEmployeeObj.employee_name : "";
     const days = Array.isArray(attendance.days) ? attendance.days : [];

     const fetchEmployees = async () => {
          try {
               const response = await fetch(`${BASE_URL}/getEmployees`, {
                    method: "GET",
                    headers: {
                         "Content-Type": "application/json",
                         "Authorization": `Bearer ${token}`,
                    },
               });
               const result = await response.json();
               console.log('👥 Employees fetched:', result.data?.length || 0);
               setEmployees(Array.isArray(result.data) ? result.data : []);
          } catch (error) {
               console.error("❌ Error fetching employees:", error);
          }
     };

     const fetchAttendance = useCallback(async () => {
          if (!selectedEmployee || !selectedMonth) {
               console.log('⚠️ Missing employee or month, skipping fetch');
               return;
          }

          try {
               setIsLoading(true);
               const url = `${BASE_URL}/getEmployeeAttendance`;
               const payload = {
                    employee_code: selectedEmployee,
                    selectedMonth: selectedMonth,
                    selectedToMonth: selectedMonth
               };

               console.log('📤 Fetching calendar attendance:', payload);

               const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                         'Content-Type': 'application/json',
                         'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload)
               });

               if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
               }

               const json = await response.json();
               console.log('📥 Calendar API Response:', json);
               console.log('📊 Days received:', json.data?.days?.length || 0);

               if (json.status && json.data) {
                    const attendanceData = {
                         ...json.data,
                         days: Array.isArray(json.data.days) ? json.data.days : []
                    };

                    console.log('✅ Setting attendance with', attendanceData.days.length, 'days');
                    setAttendance(attendanceData);

                    // Get employee type from correct path
                    const empType = json.data.employee_info?.emp_type || 'Contractual_Employee';
                    const splitFlag = json.data.employee_info?.split_shift || 'N';
                    console.log('👤 Employee Type:', empType, 'Split Shift:', splitFlag);

                    setEmployeeType(empType);

                    try {
                         const strategy = AttendanceStrategyFactory.getStrategy(empType, splitFlag);
                         console.log('✅ Strategy loaded:', strategy?.constructor?.name, 'split:', splitFlag);
                         setAttendanceStrategy(strategy);
                    } catch (strategyError) {
                         console.error('❌ Strategy error:', strategyError);
                         const defaultStrategy = AttendanceStrategyFactory.getStrategy('Contractual_Employee', 'N');
                         setAttendanceStrategy(defaultStrategy);
                    }
               } else {
                    console.error('❌ No data in response');
                    setAttendance({ days: [] });
               }
          } catch (err) {
               console.error('❌ Error fetching attendance:', err);
               setAttendance({ days: [] });
          } finally {
               setIsLoading(false);
          }
     }, [selectedEmployee, selectedMonth, BASE_URL, token]);

     useEffect(() => {
          fetchEmployees();
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     useEffect(() => {
          if (selectedEmployee && selectedMonth) {
               console.log('🔄 Triggering attendance fetch for:', selectedEmployee, selectedMonth);
               fetchAttendance();
          }
     }, [selectedEmployee, selectedMonth, fetchAttendance]);

     const getDaysInMonth = () => {
          if (!selectedMonth) return 0;
          const [year, month] = selectedMonth.split('-');
          return new Date(year, month, 0).getDate();
     };

     const getStartDayOfWeek = () => {
          if (!selectedMonth) return 0;
          const [year, month] = selectedMonth.split('-');
          return new Date(year, month - 1, 1).getDay();
     };

     const calculateWorkingHours = (punchIn, punchOut) => {
          if (!punchIn || !punchOut) return '00:00';

          const punchInDate = new Date(punchIn);
          const punchOutDate = new Date(punchOut);
          const diffMs = punchOutDate - punchInDate;

          if (diffMs <= 0) return '00:00';

          const totalMinutes = Math.floor(diffMs / (1000 * 60));
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;

          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
     };

     // Format working hours to HH:MM (remove seconds if present)
     const formatWorkingHours = (workHours) => {
          if (!workHours || workHours === '-' || workHours === '00:00') return workHours;
          // If it contains seconds (HH:MM:SS), take only HH:MM
          const parts = workHours.split(':');
          if (parts.length >= 2) {
               return `${parts[0]}:${parts[1]}`;
          }
          return workHours;
     };

     // Format schedule/punch times to HH:MM (08:00:00 -> 08:00)
     const formatTimeValue = (timeStr) => {
          if (!timeStr) return '-';
          const parts = String(timeStr).split(':');
          return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : String(timeStr);
     };

     // Robust formatter for timestamps (ISO / "YYYY-MM-DD HH:MM:SS" / "HH:MM:SS")
     const formatAnyTime = (time) => {
          if (!time) return '-';
          const s = String(time).trim();
          // if contains date part, try Date parse
          if (s.includes('T') || s.includes(' ')) {
               const parsed = new Date(s);
               if (!isNaN(parsed)) {
                    return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
               }
          }
          // fallback to HH:MM from any HH:MM:SS-like string
          return formatTimeValue(s);
     };

     const getShiftPunchIn = (shift) => {
          if (!shift) return null;
          return shift.punch_in || shift.punchIn || shift.punch_in_time || shift.punch_in_time || shift.in_time_actual || null;
     };
     const getShiftPunchOut = (shift) => {
          if (!shift) return null;
          return shift.punch_out || shift.punchOut || shift.punch_out_time || shift.out_time_actual || null;
     };
     // convert seconds to HH:MM (no seconds)
     const secondsToHM = (totalSeconds) => {
          if (!totalSeconds) return "00:00";
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
     };

     const secondsToHMS = (totalSeconds) => {
          if (!totalSeconds) return "00:00:00";
          const hours = Math.floor(totalSeconds / 3600);
          const minutes = Math.floor((totalSeconds % 3600) / 60);
          const seconds = totalSeconds % 60;
          return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
     };

     const calculateAttendanceStatus = (day, roster, holidays) => {
          // Check if strategy exists first
          if (!attendanceStrategy) {
               return { status: "-", lateMark: "-", workingHours: "-", statusColor: "#94a3b8", remarks: "" };
          }

          // Prepare employee data from attendance state
          const empData = {
               weekAttendance: attendance.weekAttendance || [],
               allDays: attendance.days || [],
               region: attendance.employee_region || 'Other'
          };

          // Get status from strategy
          const result = attendanceStrategy.calculateAttendanceStatus(day, roster, holidays, empData);

          // Get today's date in YYYY-MM-DD format using local time zone
          const now = new Date();
          const todayString = [
               now.getFullYear(),
               String(now.getMonth() + 1).padStart(2, '0'),
               String(now.getDate()).padStart(2, '0')
          ].join('-');

          // Normalize both dates to ensure clean comparison
          const dayDateNormalized = String(day.date).trim();
          const todayNormalized = todayString.trim();

          const isToday = dayDateNormalized === todayNormalized;
          const hasPunchIn = Boolean(day.punch_in);
          const hasPunchOut = Boolean(day.punch_out);

          // Get current time to check if day is still ongoing
          const currentHour = now.getHours();
          const isDayStillOngoing = currentHour < 23; // Before 11 PM

          // Override LOP to OnDuty for current day if:
          // 1. It's today
          // 2. Employee has punched in
          // 3. Status is LOP
          // 4. Day is still ongoing (optional - before 11 PM)
          if (isToday && hasPunchIn && result.status === "LOP" && isDayStillOngoing) {
               console.log('✅ OVERRIDING TO ONDUTY for date:', dayDateNormalized);
               return {
                    status: "OnDuty",
                    lateMark: result.lateMark || "-",
                    workingHours: hasPunchOut ? result.workingHours : "-",
                    statusColor: "#3b82f6", // Blue
                    remarks: "Currently on duty"
               };
          }

          return result;
     };

     // const calculateAttendanceStatus = (day, roster, holidays) => {
     //      if (!attendanceStrategy) {
     //           console.warn('⚠️ No strategy available, using defaults');
     //           const workHours = day.punch_in && day.punch_out ? calculateWorkingHours(day.punch_in, day.punch_out) : "-";
     //           return {
     //                status: day.punch_in ? "Present" : "Absent",
     //                lateMark: "-",
     //                workingHours: workHours,
     //                statusColor: day.punch_in ? "#10b981" : "#ef4444",
     //                remarks: ""
     //           };
     //      }

     //      try {
     //           const employeeData = {
     //                weekAttendance: [attendance.employee_info?.week_off],
     //                allDays: attendance.days || [],
     //                region: attendance.employee_region || 'Other'
     //           };

     //           const result = attendanceStrategy.calculateAttendanceStatus(day, roster, holidays, employeeData);
     //           // Format working hours to HH:MM
     //           if (result.workingHours && result.workingHours !== '-') {
     //                result.workingHours = formatWorkingHours(result.workingHours);
     //           }
     //           return result;
     //      } catch (error) {
     //           console.error('❌ Error calculating attendance status:', error);
     //           const workHours = day.punch_in && day.punch_out ? calculateWorkingHours(day.punch_in, day.punch_out) : "-";
     //           return {
     //                status: day.punch_in ? "Present" : "Absent",
     //                lateMark: "-",
     //                workingHours: workHours,
     //                statusColor: day.punch_in ? "#10b981" : "#ef4444",
     //                remarks: ""
     //           };
     //      }
     // };

     const renderCalendar = () => {
          if (!selectedMonth) return null;

          const daysInMonth = getDaysInMonth();
          const startDayOfWeek = getStartDayOfWeek();
          const totalCells = 42;
          const calendar = [];
          const [year, month] = selectedMonth.split('-');

          for (let i = 0; i < totalCells; i++) {
               const currentDay = i - startDayOfWeek + 1;

               if (currentDay > 0 && currentDay <= daysInMonth) {
                    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(currentDay).padStart(2, '0')}`;
                    const dayStatus = days.find(day => day.date === dateStr);
                    const roster = attendance.duty_roster?.find(r => r.attendance_date === dateStr);
                    const holidayObj = attendance.holidays?.find(h => h.holiday_date === dateStr);

                    const safeDayStatus = dayStatus || { date: dateStr, punch_in: null, punch_out: null, shifts: [] };
                    const { status, lateMark, workingHours, statusColor } = calculateAttendanceStatus(
                         safeDayStatus,
                         roster,
                         attendance.holidays
                    );

                    const punchIn = safeDayStatus?.punch_in;
                    const punchOut = safeDayStatus?.punch_out;
                    const holidayName = holidayObj?.holiday_name;

                    const isWeekoff = status === "Week Off" || status === "Weekoff";
                    const isSunday = new Date(dateStr).getDay() === 0;
                    const isDoctorSplit = (String(employeeType).toUpperCase() === 'DOCTOR') && attendance.employee_info?.split_shift === 'Y' && Array.isArray(safeDayStatus.shifts) && safeDayStatus.shifts.length > 0;


                    if (isDoctorSplit) {
                         const shift1 = (safeDayStatus.shifts || []).find(s => s.split_shift_type === 'shift_1') || {};
                         const shift2 = (safeDayStatus.shifts || []).find(s => s.split_shift_type === 'shift_2') || {};
                         const schedS1In = formatTimeValue(shift1.in_time);
                         const schedS1Out = formatTimeValue(shift1.out_time);
                         const schedS2In = formatTimeValue(shift2.in_time);
                         const schedS2Out = formatTimeValue(shift2.out_time);
                         const punchS1In = getShiftPunchIn(shift1);
                         const punchS1Out = getShiftPunchOut(shift1);
                         const punchS2In = getShiftPunchIn(shift2);
                         const punchS2Out = getShiftPunchOut(shift2);
                         const displayPunchS1In = punchS1In ? formatAnyTime(punchS1In) : '-';
                         const displayPunchS1Out = punchS1Out ? formatAnyTime(punchS1Out) : '-';
                         const displayPunchS2In = punchS2In ? formatAnyTime(punchS2In) : '-';
                         const displayPunchS2Out = punchS2Out ? formatAnyTime(punchS2Out) : '-';
                         const totalWorkedSeconds = (shift1.worked_seconds || 0) + (shift2.worked_seconds || 0);

                         // only attach hover/popup when NOT a weekly off
                         const mouseEnter = !isWeekoff
                              ? (e) => handleMouseEnter(e, currentDay, { shift1, shift2 }, null, secondsToHMS(totalWorkedSeconds))
                              : () => { };

                         calendar.push(
                              <div
                                   key={i}
                                   className={`calendar-day split-day ${isWeekoff ? 'weekoff-day' : ''} ${isSunday ? 'sunday-day' : ''}`}
                                   onMouseEnter={mouseEnter}
                                   onMouseLeave={handleMouseLeave}
                              >
                                   {status && status !== "-" && <span className="status-dot" style={{ backgroundColor: statusColor }} />
                                   }
                                   <span className="day-number">{currentDay}</span>
                                   {holidayName && <div className="holiday-name">{holidayName}</div>
                                   }

                                   <div className="day-details-split">
                                        {isWeekoff ? (
                                             <div className="status-text week-off-text">Weekly Off</div>
                                        ) : (
                                             <>
                                                  <div className="row">
                                                       <div className="col-12 col-sm-6">
                                                            <div className="shift-label">S1</div>
                                                            <div className="shift-punch-block">
                                                                 <div className="shift-punch schedule">{schedS1In} / {schedS1Out}</div>
                                                                 <div className="shift-punches punch" style={{ marginTop: '4px', fontWeight: 700, fontSize: 12, color: (punchS1In || punchS1Out) ? '#10b981' : '#6b7280' }}>
                                                                      {displayPunchS1In} / {displayPunchS1Out}
                                                                 </div>
                                                            </div>
                                                       </div>
                                                       <div className="col-12 col-sm-6">
                                                            <div className="shift-label">S2</div>
                                                            <div className="shift-punch-block">
                                                                 <div className="shift-punch schedule">{schedS2In} / {schedS2Out}</div>
                                                                 <div className="shift-punches punch" style={{ marginTop: '4px', fontWeight: 700, fontSize: 12, color: (punchS2In || punchS2Out) ? '#10b981' : '#6b7280' }}>
                                                                      {displayPunchS2In} / {displayPunchS2Out}
                                                                 </div>
                                                            </div>
                                                       </div>
                                                  </div>
                                                  <div className="split-summary" style={{ marginTop: 4, fontSize: 12 }}>
                                                       <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{secondsToHM(totalWorkedSeconds)} Hrs</span>
                                                  </div>
                                             </>
                                        )}
                                   </div>
                              </div >
                         );
                    } else {
                         calendar.push(
                              <div
                                   key={i}
                                   className={`calendar-day ${isWeekoff ? 'weekoff-day' : ''} ${isSunday ? 'sunday-day' : ''}`}
                                   onMouseEnter={(e) => handleMouseEnter(e, currentDay, punchIn, punchOut, workingHours)}
                                   onMouseLeave={handleMouseLeave}
                              >
                                   {status && status !== "-" && <span className="status-dot" style={{ backgroundColor: statusColor }} />}
                                   <span className="day-number">{currentDay}</span>
                                   {holidayName && <div className="holiday-name">{holidayName}</div>}
                                   <div className="day-details">
                                        {isWeekoff && <div className="status-text week-off-text">Weekly Off</div>}
                                        {(punchIn || punchOut) && (
                                             <div className="time-info-row">
                                                  {punchIn && <span className="time-in">{new Date(punchIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}</span>}
                                                  {punchIn && punchOut && <span className="time-separator">-</span>}
                                                  {punchOut && <span className="time-out">{new Date(punchOut).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}</span>}
                                             </div>
                                        )}
                                        {workingHours && workingHours !== "-" && workingHours !== "00:00" && <div className="working-hours">{formatWorkingHours(workingHours)} Hrs</div>}
                                        {lateMark === "Late" && <div className="late-mark-badge">⚠ Late</div>}
                                        {status && status !== "Present" && status !== "Week Off" && status !== "Weekoff" && !holidayName && status !== "-" && <div className="status-text">{status}</div>}
                                   </div>
                              </div >
                         );
                    }
               } else {
                    calendar.push(
                         <div key={i} className="calendar-day empty-day"></div>
                    );
               }
          }

          return calendar;
     };

     const handleMouseEnter = (e, currentDay, punchIn, punchOut, workingHours) => {
          const position = e.target.getBoundingClientRect();

          // single punch flow
          if (punchIn && punchOut) {
               const formattedHours = workingHours || calculateWorkingHours(punchIn, punchOut);
               setPopupDetails({
                    show: true,
                    punchIn,
                    punchOut,
                    day: currentDay,
                    workHours: formatWorkingHours(formattedHours),
                    position: {
                         top: position.bottom + window.scrollY + 5,
                         left: position.left + window.scrollX,
                    }
               });
               return;
          }
          // split-shift flow: punchIn will be object { shift1, shift2 }
          if (punchIn && typeof punchIn === 'object' && (punchIn.shift1 || punchIn.shift2)) {
               const s1 = punchIn.shift1 || {};
               const s2 = punchIn.shift2 || {};
               const totalWorkedSeconds = (s1.worked_seconds || 0) + (s2.worked_seconds || 0);
               setPopupDetails({
                    show: true,
                    punchIn: s1.punch_in || null,
                    punchOut: s2.punch_out || null,
                    day: currentDay,
                    workHours: secondsToHMS(totalWorkedSeconds),
                    position: {
                         top: position.bottom + window.scrollY + 5,
                         left: position.left + window.scrollX,
                    },
                    shift1: s1,
                    shift2: s2
               });
          }
     };
     const handleMouseLeave = () => {
          setPopupDetails({ show: false, punchIn: '', punchOut: '', day: null, position: {}, workHours: '' });
     };


     const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

     const presentCount = days.filter(day => {
          const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
          const { status } = calculateAttendanceStatus(day, roster, attendance.holidays);
          return status === "Present";
     }).length;

     const absentCount = days.filter(day => {
          const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
          const { status } = calculateAttendanceStatus(day, roster, attendance.holidays);
          return status === "Absent";
     }).length;

     const halfDayCount = days.filter(day => {
          const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
          const { status } = calculateAttendanceStatus(day, roster, attendance.holidays);
          return status === "Half Day";
     }).length;

     const lopCount = days.filter(day => {
          const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
          const { status } = calculateAttendanceStatus(day, roster, attendance.holidays);
          return status === "LOP";
     }).length;

     const weekoffCount = days.filter(day => {
          const roster = attendance.duty_roster?.find(r => r.attendance_date === day.date);
          const { status } = calculateAttendanceStatus(day, roster, attendance.holidays);
          return status === "Week Off" || status === "Weekoff";
     }).length;

     const holidayCount = days.filter(day => {
          const holidayObj = attendance.holidays?.find(h => h.holiday_date === day.date);
          return holidayObj;
     }).length;

     const [isInputFocused, setIsInputFocused] = useState(false);

     return (
          <div className="employee-attendance-calendar-wrapper responsive-card">
               <div className="attendance-header">
                    <h2>Employee Attendance Calendar</h2>
                    {employeeType && (
                         <span style={{ marginTop: "5px" }}>
                              {employeeType}
                         </span>
                    )}
               </div>

               <div className="attendance-main-card">
                    <div className="filters-container row">
                         <div className="col-12 col-md-3 filter-group employee-group">
                              <label>
                                   <FaUser /> Employee
                              </label>
                              <input
                                   className="filter-input"
                                   type="text"
                                   placeholder="Select Employee"
                                   list="employees"
                                   value={isInputFocused ? '' : employeeInputValue}
                                   onChange={e => {
                                        if (isInputFocused) {
                                             // When focused, handle selection
                                             const found = employees.find(emp =>
                                                  `${emp.employee_code} - ${emp.employee_name}` === e.target.value
                                             );
                                             if (found) {
                                                  setEmployeeInputValue(`${found.employee_code} - ${found.employee_name}`);
                                                  setSelectedEmployee(found.employee_code);
                                                  setAttendance({ days: [] });
                                                  setIsInputFocused(false);
                                                  console.log('👤 Employee selected:', found.employee_code, found.employee_name);
                                             }
                                        } else {
                                             // Normal typing behavior
                                             setEmployeeInputValue(e.target.value);
                                             const found = employees.find(emp =>
                                                  `${emp.employee_code} - ${emp.employee_name}` === e.target.value
                                             );
                                             if (found) {
                                                  setSelectedEmployee(found.employee_code);
                                                  setAttendance({ days: [] });
                                             } else {
                                                  setSelectedEmployee("");
                                             }
                                        }
                                   }}
                                   onFocus={() => setIsInputFocused(true)}
                                   onBlur={() => setIsInputFocused(false)}
                                   autoComplete="off"
                                   autoCorrect="off"
                                   autoCapitalize="off"
                                   spellCheck="false"
                              />
                              <datalist id="employees">
                                   {employees.map(emp => (
                                        <option
                                             key={emp.emp_id}
                                             value={`${emp.employee_code} - ${emp.employee_name}`}
                                        />
                                   ))}
                              </datalist>
                         </div>

                         <div className="col-12 col-md-2 filter-group month-group">
                              <label>
                                   <FaCalendarAlt /> Month
                              </label>
                              <input
                                   type="month"
                                   className="filter-input"
                                   min="2025-08"
                                   value={selectedMonth}
                                   onChange={e => {
                                        console.log('📅 Month selected:', e.target.value);
                                        setSelectedMonth(e.target.value);
                                   }}
                                   disabled={!selectedEmployee}
                              />
                         </div>


                         <div className="col-12 col-md-2 filter-group action-group">
                              <button
                                   className="list-view-btn"
                                   onClick={() => navigate('/employee-attendance', {
                                        state: {
                                             selectedEmployee: selectedEmployee,
                                             selectedEmployeeName: selectedEmployeeName,
                                             selectedMonth: selectedMonth,
                                             employeeType: employeeType
                                        }
                                   })}
                                   disabled={isLoading}
                              >
                                   <FaList /> List View
                              </button>
                         </div>
                    </div>

                    {isLoading ? (
                         <div className="no-data-message">
                              <p>Loading attendance data...</p>
                         </div>
                    ) : days.length > 0 ? (
                         <>
                              <div className="calendar-container">
                                   <div className="calendar-weekdays">
                                        {daysOfWeek.map((day, index) => (
                                             <div key={index} className="weekday-header">
                                                  {day}
                                             </div>
                                        ))}
                                   </div>

                                   <div className="calendar-grid">
                                        {renderCalendar()}
                                   </div>
                              </div>

                              {popupDetails.show && (
                                   <div
                                        className="attendance-popup"
                                        style={{
                                             top: popupDetails.position.top,
                                             left: popupDetails.position.left,
                                        }}
                                   >
                                        {popupDetails.shift1 || popupDetails.shift2 ? (
                                             <>
                                                  <div className="popup-time">S1 Sch: {popupDetails.shift1 ? formatTimeValue(popupDetails.shift1.in_time) + ' / ' + formatTimeValue(popupDetails.shift1.out_time) : '-'}</div>
                                                  <div className="popup-time">S1 Punch: {popupDetails.shift1 ? formatAnyTime(getShiftPunchIn(popupDetails.shift1)) + ' / ' + formatAnyTime(getShiftPunchOut(popupDetails.shift1)) : '-'}</div>
                                                  <div style={{ height: 6 }} />
                                                  <div className="popup-time">S2 Sch: {popupDetails.shift2 ? formatTimeValue(popupDetails.shift2.in_time) + ' / ' + formatTimeValue(popupDetails.shift2.out_time) : '-'}</div>
                                                  <div className="popup-time">S2 Punch: {popupDetails.shift2 ? formatAnyTime(getShiftPunchIn(popupDetails.shift2)) + ' / ' + formatAnyTime(getShiftPunchOut(popupDetails.shift2)) : '-'}</div>
                                                  <div className="popup-hours">Total: {popupDetails.workHours} Hrs</div>
                                             </>
                                        ) : (
                                             <>
                                                  <div className="popup-time">In: {popupDetails.punchIn ? new Date(popupDetails.punchIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '-'}</div>
                                                  <div className="popup-time">Out: {popupDetails.punchOut ? new Date(popupDetails.punchOut).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '-'}</div>
                                                  <div className="popup-hours">Total: {popupDetails.workHours} Hrs</div>
                                             </>
                                        )}
                                   </div>
                              )}

                              <div className="calendar-legend">
                                   <div className="legend-item present">
                                        <span className="legend-dot"></span>
                                        Present: {presentCount}
                                   </div>
                                   <div className="legend-item absent">
                                        <span className="legend-dot"></span>
                                        Absent: {absentCount}
                                   </div>
                                   <div className="legend-item half-day">
                                        <span className="legend-dot"></span>
                                        Half Day: {halfDayCount}
                                   </div>
                                   <div className="legend-item week-off">
                                        <span className="legend-dot"></span>
                                        Weekly Off: {weekoffCount}
                                   </div>
                                   <div className="legend-item holiday">
                                        <span className="legend-dot"></span>
                                        Holiday: {holidayCount}
                                   </div>
                                   {lopCount > 0 && (
                                        <div className="legend-item lop">
                                             <span className="legend-dot"></span>
                                             LOP: {lopCount}
                                        </div>
                                   )}
                              </div>
                         </>
                    ) : (
                         selectedEmployee && selectedMonth ? (
                              <div className="no-data-message">
                                   <p>No attendance records found for {selectedEmployeeName} in {selectedMonth}</p>
                              </div>
                         ) : (
                              <div className="no-data-message">
                                   <p>Please select employee and month to view attendance calendar</p>
                              </div>
                         )
                    )}
               </div>
          </div>
     );
}

export default EmployeeAttendanceCalendar;